import { LoggableAPI } from '../models/protocols/loggable-api';
import { inject, Injectable } from '@angular/core';
import { ApiClient } from './api-client';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { Observable, throwError } from 'rxjs';
import { ODataResponse } from '../models/protocols/odata-response';
import { Endpoints } from './endpoints';
import { catchError, map } from 'rxjs/operators';
import { CustomError } from '../models/shared/custom-error';
import { EmployerUser } from '../models/users/dto/employer-user';
import { EmployerMemberRecord } from '../models/users/dto/employer-member-record';
import { CreateEmployerUserRequest } from '../models/users/requests/create-employer-user-request';
import { Role } from '../models/roles/role';
import { ChangeEmailRequest } from '../models/users/requests/change-email-request';
import { HttpResponse } from '@angular/common/http';
import { EmployerPlan } from '../models/employers/plans/employer-plan';
import { EmployerPlanDesignatedUnit } from '../models/employers/plans/employer-plan-designated-unit';
import { IpCreateDesignatedUnitRequest } from '../models/employers/plans/ip-create-designated-unit-request';
import { EmployerPlanDesignatedUnitVersion } from '../models/employers/plans/employer-plan-designated-unit-version';
import { EmployerSearchResult } from '../models/employers/dto/employer-search-result';
import { DesignatedUnitSearchResult } from '../models/employers/plans/designated-unit-search-result';
import { CreateMemberRequest } from '../models/users/requests/create-member-request';
import { MemberUser } from '../models/users/dto/member-user';
import { Employer } from '../models/users/dto/employer';
import { EmployerLogo } from '../models/users/dto/employer-logo';
import { EmployerCRANumberInUseResponse } from '../models/employers/dto/employer-cra-number-in-use-response';
import { CreateEmployerReq } from '../models/employers/dto/create-employer-req';
import { IpEmployerSubmission } from '../models/submissions/employer-submissions/ip-employer-submission';
import { UpdateEmployerReq } from '../models/employers/dto/update-employer-req';
import { UpdateEmployerAddressRequest } from '../models/users/requests/submissionRequests/update-employer-address-request';
import { UpdateEmployerUserRequest } from '../models/users/requests/update-employer-user-request';
import { IpUpdateEmployerContactsRequest } from '../models/users/requests/submissionRequests/ip-update-employer-contacts-request';
import { EmployerRecordComment } from '../models/employers/employer-record-comment';
import { EmployerRecordCommentRequest } from '../models/employers/employer-record-comment-request';
import { Mentionable } from '../models/base/mentionable';
import { InternalUserCommentReadReceiptRequest } from '../models/users/requests/internal-user-comment-read-receipt-request';
import { EpUpdateEmployerContactsRequest } from '../models/users/requests/submissionRequests/ep-update-employer-contacts-request';
import { EpEmployerSubmission } from '../models/submissions/employer-submissions/ep-employer-submission';
import { IpCreateEmploymentStatusChangeSubmissionRequest } from '../features/employment-status-change/models/ip-create-employment-status-change-submission-request';

@Injectable({
  providedIn: 'root'
})
export class EmployersAPI implements LoggableAPI {
  constructor() {}

  private apiClient = inject(ApiClient);

  public serviceName = 'Employers';

  public ipGetEmployerById(employerId: number, opts = new ODataQueryOptions()): Observable<Employer> {
    const url = Endpoints.ipGetEmployerById(employerId);
    return this.apiClient.getOdataObj<Employer>(url, Employer, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployer(opts = new ODataQueryOptions()): Observable<Employer> {
    const url = Endpoints.epGetEmployer();
    return this.apiClient.getOdataObj<Employer>(url, Employer, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployers(oDataQueryOptions: ODataQueryOptions): Observable<ODataResponse<Employer>> {
    const url = Endpoints.ipGetEmployers();
    return this.apiClient.getOdata<Employer>(url, Employer, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerLogo(): Observable<EmployerLogo> {
    const url = Endpoints.epGetEmployerLogo();
    return this.apiClient.getObj(EmployerLogo, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetEmployerLogo(employerId: number): Observable<EmployerLogo> {
    const url = Endpoints.mpGetEmployerLogo(employerId);
    return this.apiClient.getObj(EmployerLogo, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerUsers(
    oDataQueryOptions: ODataQueryOptions,
    employerId: number
  ): Observable<ODataResponse<EmployerUser>> {
    const url = Endpoints.ipGetEmployerUsers(employerId);
    oDataQueryOptions.setExpand('Roles($expand=Role)');
    return this.apiClient.getOdata<EmployerUser>(url, EmployerUser, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerMembers(
    oDataQueryOptions: ODataQueryOptions,
    employerId: number
  ): Observable<ODataResponse<EmployerMemberRecord>> {
    const url = Endpoints.ipGetEmployerMembers(employerId);
    oDataQueryOptions.setExpand('EmploymentStatus');
    return this.apiClient.getOdata<EmployerMemberRecord>(url, EmployerMemberRecord, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getEmployerUsers(oDataQueryOptions: ODataQueryOptions): Observable<ODataResponse<EmployerUser>> {
    const url = Endpoints.getEmployerUsers();
    oDataQueryOptions.setExpand('Roles');
    return this.apiClient.getOdata<EmployerUser>(url, EmployerUser, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateEmployerUser(req: CreateEmployerUserRequest, employerId: number): Observable<EmployerUser> {
    const url = Endpoints.ipCreateEmployerUser(employerId);
    return this.apiClient.postObj(EmployerUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createEmployerUser(req: CreateEmployerUserRequest): Observable<EmployerUser> {
    const url = Endpoints.createEmployerUser();
    return this.apiClient.postObj(EmployerUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerRoles(
    odataQueryOptions: ODataQueryOptions = new ODataQueryOptions()
  ): Observable<ODataResponse<Role>> {
    const url = Endpoints.ipGetEmployerRoles();
    // todo - we don't have an employer specific permission for this.
    const permissionHeaders = { permissions: [0] };
    return this.apiClient.getOdata(url, Role, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epCreateEmployee(req: CreateMemberRequest): Observable<MemberUser> {
    const url = Endpoints.epCreateEmployee();
    return this.apiClient.postObj(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerRoles(
    odataQueryOptions: ODataQueryOptions = new ODataQueryOptions()
  ): Observable<ODataResponse<Role>> {
    const url = Endpoints.epGetEmployerRoles();
    return this.apiClient.getOdata(url, Role, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerUser(employerId: number, userId: number): Observable<EmployerUser> {
    const url = Endpoints.ipGetEmployerUser(employerId, userId);
    const odataQueryOptions = new ODataQueryOptions();
    odataQueryOptions.setExpand('Roles');
    return this.apiClient.getOdataObj<EmployerUser>(url, EmployerUser, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerUser(userId: number): Observable<EmployerUser> {
    const url = Endpoints.epGetEmployerUser(userId);
    const odataQueryOptions = new ODataQueryOptions();
    odataQueryOptions.setExpand('Roles');
    return this.apiClient.getOdataObj<EmployerUser>(url, EmployerUser, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployerUser(req: UpdateEmployerUserRequest): Observable<EmployerUser> {
    const url = Endpoints.ipUpdateEmployerUser(req.employerId, req.id);
    return this.apiClient.putObj(EmployerUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epUpdateEmployerUser(req: UpdateEmployerUserRequest): Observable<EmployerUser> {
    const url = Endpoints.updateEmployerUser(req.id);
    return this.apiClient.putObj(EmployerUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateEmployerUser(u: EmployerUser): Observable<EmployerUser> {
    const url = Endpoints.updateEmployerUser(u.id);
    return this.apiClient.putObj(EmployerUser, url, u).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public forceChangeEmail(u: EmployerUser, req: ChangeEmailRequest): Observable<EmployerUser> {
    const url = Endpoints.employerForceChangeEmail(u);
    return this.apiClient.postObj(EmployerUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // This endpoint works to both force reset another Employer User's password, and resend the welcome email.
  public forceResetPassword(u: EmployerUser): Observable<HttpResponse<any>> {
    const url = Endpoints.employerForceChangePassword(u);
    return this.apiClient.simpleBodylessPost(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getInternalEmployerPlan(employerPlanId: string): Observable<EmployerPlan> {
    const url = Endpoints.getInternalEmployerPlan(employerPlanId);
    const odataQueryOptions = new ODataQueryOptions();
    odataQueryOptions.setExpand(
      'DesignatedUnits($expand=DesignatedUnitType, Versions($expand=ContributionRateBasisType, MatchedContributionRateType, UnmatchedContributionRateType, LessThanFullTimeParticipationType))',
      'EmployerPlanType',
      'RemittanceFrequencyType',
      'Comments($expand=Mentions)'
    );
    return this.apiClient.getOdataObj<EmployerPlan>(url, EmployerPlan, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getEmployerEmployerPlan(employerPlanId: string): Observable<EmployerPlan> {
    const url = Endpoints.getEmployerEmployerPlan(employerPlanId);
    const odataQueryOptions = new ODataQueryOptions();
    odataQueryOptions.setExpand(
      'DesignatedUnits($expand=DesignatedUnitType, Versions($expand=ContributionRateBasisType, MatchedContributionRateType, UnmatchedContributionRateType, LessThanFullTimeParticipationType))',
      'EmployerPlanType',
      'RemittanceFrequencyType'
    );
    return this.apiClient.getOdataObj<EmployerPlan>(url, EmployerPlan, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateEmployerPlan(ep: EmployerPlan): Observable<EmployerPlan> {
    const url = Endpoints.updateEmployerPlan(ep.id.toString(10));
    return this.apiClient.putObj(EmployerPlan, url, ep).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerPlanDesignatedUnits(
    employerPlanId: number,
    odataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<EmployerPlanDesignatedUnit>> {
    const url = Endpoints.ipGetEmployerPlanDesignatedUnits(employerPlanId);
    return this.apiClient.getOdata<EmployerPlanDesignatedUnit>(url, EmployerPlanDesignatedUnit, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerPlanDesignatedUnits(
    employerPlanId: number,
    odataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<EmployerPlanDesignatedUnit>> {
    const url = Endpoints.epGetEmployerPlanDesignatedUnits(employerPlanId);
    return this.apiClient.getOdata<EmployerPlanDesignatedUnit>(url, EmployerPlanDesignatedUnit, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerPlanDesignatedUnitById(
    employerPlanId: number,
    designatedUnitId: number,
    odataQueryOptions: ODataQueryOptions
  ): Observable<EmployerPlanDesignatedUnit> {
    const url = Endpoints.epGetEmployerPlanDesignatedUnitById(employerPlanId, designatedUnitId);
    return this.apiClient
      .getOdataObj<EmployerPlanDesignatedUnit>(url, EmployerPlanDesignatedUnit, odataQueryOptions)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipCreateDesignatedUnit(
    employerPlanId: number,
    req: IpCreateDesignatedUnitRequest
  ): Observable<EmployerPlanDesignatedUnit> {
    const url = Endpoints.ipCreateEmployerPlanDesignatedUnit(employerPlanId);
    return this.apiClient.postObj(EmployerPlanDesignatedUnit, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployerPlanDesignatedUnit(req: EmployerPlanDesignatedUnit): Observable<EmployerPlanDesignatedUnit> {
    const url = Endpoints.ipUpdateEmployerPlanDesignatedUnit(req.employerPlanId, +req.id);
    return this.apiClient.putObj(EmployerPlanDesignatedUnit, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateEmployerPlanDesignatedUnitVersion(
    req: EmployerPlanDesignatedUnitVersion
  ): Observable<EmployerPlanDesignatedUnitVersion> {
    const url = Endpoints.ipCreateEmployerPlanDesignatedUnitVersion(req.employerPlanId, req?.designatedUnitId);
    return this.apiClient.postObj(EmployerPlanDesignatedUnitVersion, url, req).pipe(
      map(res => {
        // The API response omits employerPlanId and designatedUnitId; restore them from the request
        // so subsequent updates can build the URL.
        res.employerPlanId = req.employerPlanId;
        res.designatedUnitId = req.designatedUnitId;
        return res;
      }),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateEmployerPlanDesignatedUnitVersion(
    req: EmployerPlanDesignatedUnitVersion
  ): Observable<EmployerPlanDesignatedUnitVersion> {
    const url = Endpoints.ipUpdateEmployerPlanDesignatedUnitVersion(req.employerPlanId, req.designatedUnitId, req.id);
    return this.apiClient.putObj(EmployerPlanDesignatedUnitVersion, url, req).pipe(
      map(res => {
        // The API response omits employerPlanId and designatedUnitId; restore them from the request
        // so subsequent updates can build the URL.
        res.employerPlanId = req.employerPlanId;
        res.designatedUnitId = req.designatedUnitId;
        return res;
      }),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public searchEmployersByIdOrName(
    odataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<EmployerSearchResult>> {
    const url = Endpoints.searchEmployersByIdOrName();
    return this.apiClient.getOdata<EmployerSearchResult>(url, EmployerSearchResult, odataQueryOptions, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public searchInternalActiveDesignatedUnitsByIdOrName(
    odataQueryOptions: ODataQueryOptions,
    planId: number
  ): Observable<ODataResponse<DesignatedUnitSearchResult>> {
    const url = Endpoints.searchInternalActiveDesignatedUnits(planId);
    return this.apiClient
      .getOdata<DesignatedUnitSearchResult>(url, DesignatedUnitSearchResult, odataQueryOptions, null, undefined)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public searchEmployerActiveDesignatedUnitsByIdOrName(
    odataQueryOptions: ODataQueryOptions,
    planId: number
  ): Observable<ODataResponse<DesignatedUnitSearchResult>> {
    const url = Endpoints.searchEmployerActiveDesignatedUnits(planId);
    return this.apiClient
      .getOdata<DesignatedUnitSearchResult>(url, DesignatedUnitSearchResult, odataQueryOptions, null, undefined)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public getEmployerCRANumberInUse(craNumber: string): Observable<EmployerCRANumberInUseResponse> {
    const url = Endpoints.getEmployerCRANumberInUse(craNumber);
    return this.apiClient.getObj(EmployerCRANumberInUseResponse, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateEmployer(req: CreateEmployerReq): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipCreateEmployer();
    return this.apiClient.postObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployer(req: UpdateEmployerReq): Observable<Employer> {
    const url = Endpoints.ipUpdateEmployer(req.id);
    return this.apiClient.putObj(Employer, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployerAddress(req: UpdateEmployerAddressRequest): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipUpdateEmployerAddress();
    return this.apiClient.postObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployerContacts(req: IpUpdateEmployerContactsRequest): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipUpdateEmployerContacts();
    return this.apiClient.postObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateEmploymentStatusChangeSubmission(
    req: IpCreateEmploymentStatusChangeSubmissionRequest
  ): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipCreateEmploymentStatusChangeSubmission();
    return this.apiClient.postObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epUpdateEmployerContacts(req: EpUpdateEmployerContactsRequest): Observable<EpEmployerSubmission> {
    const url = Endpoints.epUpdateEmployerContacts();
    return this.apiClient.postObj(EpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerCommentMentionables(opts: ODataQueryOptions): Observable<ODataResponse<Mentionable>> {
    const url = Endpoints.ipGetEmployerCommentMentionables();
    opts.setExpand('ProfileBadgeConfiguration');
    return this.apiClient.getOdata<Mentionable>(url, Mentionable, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipAddEmployerRecordComment(req: EmployerRecordCommentRequest): Observable<EmployerRecordComment[]> {
    const url = Endpoints.ipAddEmployerRecordComment(req.employerId);
    return this.apiClient.simplePost(url, req).pipe(
      map(res => {
        const comments = window?.injector?.Deserialize?.arrayOf(EmployerRecordComment, res.body);
        return comments.sort((a, b) => {
          // @ts-ignore
          return new Date(b.createdDate) - new Date(a.createdDate);
        });
      }),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetCommentsForEmployer(employerId: string): Observable<ODataResponse<EmployerRecordComment>> {
    const url = Endpoints.ipGetCommentsForEmployer(employerId);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand('mentions');
    return this.apiClient.getOdata<EmployerRecordComment>(url, EmployerRecordComment, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSetEmployerCommentsAsRead(req: InternalUserCommentReadReceiptRequest): Observable<HttpResponse<any>> {
    const url = Endpoints.ipCreateEmployerCommentReadReceipts();
    return this.apiClient.simplePost(url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }
}
